import { Field } from "redux-form/immutable"
import CheckBoxField from "forms/components/CheckBoxField"
import PillToggleField from "forms/components/PillToggleField"
import InputField from "forms/components/InputField"

export const helpText = {
    combineCampaignText: `With a combined campaign, your advocates
                        will be able to click one button in order to participate
                        in multiple campaigns at once. For example, if you wanted
                        to have your advocates comment on a regulation and
                        submit a letter to the White House at the same time, a combined
                        campaign makes that possible. Combined campaigns support
                        "Write a Letter", "Sign a Petition", "Comment on Regulation",
                        and "Write to the White House" campaign types. Make sure to have
                        created the other campaigns you'd like to combine before
                        creating this combined campaign.`,

    targetSectionText: `When choosing which individuals (legislators,
                        executives, or custom) to have your advocates interact
                        with, you can limit your selection either by who your
                        campaign is targeting or by which of your advocates
                        are allowed to see the campaign. Not all
                        options are supported by all campaign types, but for
                        most campaigns, first choose whether you'd like to
                        target Officials or a custom target. Next, limit the
                        individuals that you'd like to target, maybe by a
                        particular type of legislator or a custom search (like
                        legislators from a particular state or who have one of
                        your facilities in their district.) Finally, if
                        available, you limit the advocates that you'd like to
                        participate to just advocates that have a particular
                        custom field or who have previously participated in a
                        campaign.`,

    limitParticipantsSectionText: `Choose whether you would like to limit which
                        of your advocates should be allowed to see this
                        campaign. To limit the participants, choose custom list and
                        add filters. These filters will both apply to the supporters
                        currently in your database as well as new supporters that register
                        in the future.`,

    draftSectionText: `Now that you've chosen the targets and participants
                        for this campaign, you can now set up the draft message
                        for that campaign. For most campaign types, each
                        supporter will see the same message (with
                        placeholders); however, for some messages, you might
                        want to set it so that different supporters use
                        entirely different drafts. For example, you might want
                        legislators who have voted one way to receive one
                        message and all others a different one, or maybe you
                        have a list of legislators that you would like to send
                        a message to and have anyone not on that list receive a
                        different message. To do this, you can choose to
                        generate multiple drafts and then choose a set of
                        targets to receive a different message from everyone
                        else.`,

    tweetDescription: `Write the draft of the tweet(s) you would like
                        your advocates to send. You can click the "Add Option"
                        button to add multiple drafts of tweets that your advocates
                        can choose between.
                        If you'd like to include a link you should type that directly
                        into the field. Lastly, make sure you don't exceed the character limit.`,

    regulationDescription: `Please enter a valid regulation ID for your campaign. An ID must
                            correspond with an active regulation on the regulations.gov
                            website. Campaigns with invalid ID's will not be processed. Please also
                            decide how your organization's name will appear on regulatory
                            comments.`,

    tagBillDescription: `Tag ${DjangIO.app.models.QuorumDataType.bill.plural} to your campaign.
        You can see the campaigns tagged to a certain ${DjangIO.app.models.QuorumDataType.bill.singular} on the
        ${DjangIO.app.models.QuorumDataType.bill.singular}'s profile. This will not change the public campaign's page.`,

    tagBillTooltip: `Choose one or more bills. `, // TODO

    tagRegulationDescription: `Tag ${DjangIO.app.models.QuorumDataType.regulation.plural} to your campaign.
        You can see the campaigns tagged to a certain ${DjangIO.app.models.QuorumDataType.regulation.singular} on the
        ${DjangIO.app.models.QuorumDataType.regulation.singular}'s profile. This will not change the public campaign's page.`,

    tagRegulationTooltip: `Choose one or more ${DjangIO.app.models.QuorumDataType.regulation.plural}. `,

    messagePlaceholder: `Compose your message. Click Insert Placeholder to use placeholders...`,

    reorderableDescription: `Registration forms allow you to bring in new advocates and collect key information about
                            them to store in Quorum. The information that you can collect is customizable through
                            choosing which fields and in what order to display on the form. To get started, click
                            "+ Add Field to Form" below and select from basic fields that we have prepopulated and
                            custom fields that your team has made. Drag and drop to reorder how they will display
                            on the form and click the pencil to edit their settings.`,

    reorderableDonationDescription: `Donation forms allow you to
                                     raise funds from PAC eligibles and collect key information about them to store in
                                     Quorum. The information that you can collect is customizable through choosing
                                     which fields and in what order to display on the form. To get started, click "Add
                                     Field to Form" below and select from basic fields that we have pre-populated and
                                     custom fields that your team has made. Drag and drop to reorder how they will
                                     display on the form and click the pencil to edit their settings.`,

    donationAmountsDescription:
        "Donation tiers are suggested levels of giving. You must create at least one-tier. " +
        "Next to each suggested amount, you’re able to include a description of what that " +
        "amount works to accomplish.",
}

const contactFirstName = {
    slug: "contact_first_name",
    value: "{{contact_first_name}}",
    name: "Contact's First Name (Joe)",
}

export const placeholdersSupporterFirstNameOnly = [
    {
        name: "Contacts",
        items: [contactFirstName],
    },
]

export const placeholdersSupportersOnly = [
    {
        name: "Contacts",
        items: [
            contactFirstName,
            {
                slug: "contact_last_name",
                value: "{{contact_last_name}}",
                name: "Contact's Last Name (Brown)",
            },
            {
                slug: "contact_full_name",
                value: "{{contact_full_name}}",
                name: "Contact's Full Name (Joe Brown)",
            },
            {
                slug: "contact_state_name",
                value: "{{contact_state_name}}",
                name: "Contact's State (Wisconsin)",
            },
            {
                slug: "contact_address",
                value: "{{contact_address}}",
                name: "Contact's Address (1465 G Street Washington DC)",
            },
            {
                slug: "contact_email",
                value: "{{contact_email}}",
                name: "Contact's Email (joe@quorum.us)",
            },
            {
                slug: "contact_state_capital",
                value: "{{contact_state_capital}}",
                name: "Contact's State Capital (Madison)",
            },
            {
                slug: "contact_phone_number",
                value: "{{contact_phone_number}}",
                name: "Contact's Phone Number (818-274-4277)",
            },
            {
                slug: "contact_quorum_id",
                value: "{{contact_quorum_id}}",
                name: "Contact's Quorum ID (12345)",
            },
        ],
    },
]

export const fullPlaceholders = [
    ...placeholdersSupportersOnly,
    {
        name: "Targets",
        items: [
            {
                slug: "target_first_name",
                value: "{{target_first_name}}",
                name: "Target's First Name (Andrew)",
            },
            {
                slug: "target_last_name",
                value: "{{target_last_name}}",
                name: "Target's Last Name (Abrams)",
            },
            {
                slug: "target_full_name",
                value: "{{target_full_name}}",
                name: "Target's Full Name (Sen. Joe Abrams (R-TX-3))",
            },
            {
                slug: "target_appropriate_greeting",
                value: "{{target_appropriate_greeting}}",
                name: "Target's Appropriate Greeting (Sen. Abrams, George, etc.)",
            },
            {
                slug: "target_title",
                value: "{{target_title}}",
                name: "Target's Title (Senator, Representative, etc.)",
            },
            {
                slug: "target_pronoun",
                value: "{{target_pronoun}}",
                name: "Target's Pronoun (He/She)",
            },
            {
                slug: "target_prounoun_object",
                value: "{{target_pronoun_object}}",
                name: "Target's Objective Pronoun (Him/Her)",
            },
            {
                slug: "target_prounoun_possessive",
                value: "{{target_pronoun_possessive}}",
                name: "Target's Possessive Pronoun (His/Hers)",
            },
        ],
    },
]

export const fullPlaceholdersWithTwitter = [
    ...placeholdersSupportersOnly,
    {
        name: "Targets",
        items: [
            {
                slug: "target_first_name",
                value: "{{target_first_name}}",
                name: "Target's First Name (Andrew)",
            },
            {
                slug: "target_last_name",
                value: "{{target_last_name}}",
                name: "Target's Last Name (Abrams)",
            },
            {
                slug: "target_full_name",
                value: "{{target_full_name}}",
                name: "Target's Full Name (Sen. Joe Abrams (R-TX-3))",
            },
            {
                slug: "target_appropriate_greeting",
                value: "{{target_appropriate_greeting}}",
                name: "Target's Appropriate Greeting (Sen. Abrams, George, etc.)",
            },
            {
                slug: "target_title",
                value: "{{target_title}}",
                name: "Target's Title (Senator, Representative, etc.)",
            },
            {
                slug: "target_pronoun",
                value: "{{target_pronoun}}",
                name: "Target's Pronoun (He/She)",
            },
            {
                slug: "target_prounoun_object",
                value: "{{target_pronoun_object}}",
                name: "Target's Objective Pronoun (Him/Her)",
            },
            {
                slug: "target_prounoun_possessive",
                value: "{{target_pronoun_possessive}}",
                name: "Target's Possessive Pronoun (His/Hers)",
            },
            {
                slug: "target_twitter_handle",
                value: "{{target_twitter_handle}}",
                name: "Target's Twitter Handle (@potus)",
            },
        ],
    },
]

export const placeholders = {
    placeholdersSupporterFirstNameOnly,
    placeholdersSupportersOnly,
    fullPlaceholders,
    fullPlaceholdersWithTwitter,
}

export const defaultSubfields = [
    {
        component: Field,
        name: "props.label",
        props: {
            component: InputField,
            label: "Override Label for Form",
            placeholder: 'e.g. "Field Name"',
        },
    },
    {
        component: Field,
        name: "props.optional_field",
        props: {
            buttonText: "Required",
            component: CheckBoxField,
            largeButton: false,
            // Format and parse functions are necessary because
            // if checked the box for 'Required', want 'optional_field=false'
            // and if uncheck the box for 'Required', want 'optional_field=true'
            format: (value) => !value,
            parse: (value) => !value,
        },
    },
    {
        component: Field,
        name: "props.required_value",
        props: {
            component: PillToggleField,
            format: (value) => Boolean(value),
            // If they unselect this toggle, set `required_value=null` so no values are required
            // Cannot set 'required_value=false', or a false value will be required
            parse: (value) => (value ? true : null),
            onExplanation: "They are required to agree or answer yes",
            offExplanation: "They are not required to agree or answer yes",
        },
    },
    // TODO: uncomment when 'show_filled_field', 'conditional_parent_field' are implemented in Grassroots frontend
    // { component: () => <hr /> },
    // {
    //     component: Field,
    //     name: 'props.show_filled_field',
    //     props: {
    //         buttonText: 'Re-ask if already answered',
    //         component: CheckBoxField,
    //         largeButton: false,
    //     }
    // },
    // { component: () => <hr /> },
    // {
    //     component: Field,
    //     name: 'props.conditional_parent_field',
    //     props: {
    //         component: SelectField,
    //         choices: basicInfoToggles.concat(customFieldToggles),
    //         clearable: false,
    //         displayErrorWithoutTouch: false,
    //         label: 'Which field is it based on?',
    //         parse: (registrationField) => registrationField.value
    //     },
    // },
    // {
    //     component: Field,
    //     name: 'props.conditional_parent_value',
    //     props: {
    //         component: InputField,
    //         label: 'What value of that field should to be shown on?',
    //         placeholder: 'e.g. "Field Name"',
    //     }
    // },
]

export const textOptInSubfields = [
    {
        component: Field,
        name: "props.allow_choices",
        props: {
            component: PillToggleField,
            format: (value) => Boolean(value),
            parse: (value) => (value ? true : null),
            onExplanation: "Supporters must manually elect to receive texts from you",
            offExplanation: "Supporters will be automatically signed up for texts",
        },
    },
    {
        component: Field,
        name: "props.optional_field",
        props: {
            buttonText: "Required",
            component: CheckBoxField,
            largeButton: false,
            format: (value) => !value,
            parse: (value) => !value,
        },
    },
]

export const subfieldsByRegistrationFieldType = {
    default: defaultSubfields,
    [DjangIO.app.grassroots.types.GrassrootsRegistrationField.text_opt_in.value]: textOptInSubfields,
}

export const defaultPropsByRegistrationFieldType = {
    default: {},
}

export const DEFAULT_DONATION_AMOUNTS = [
    {
        label: "Bronze",
        value: 10000,
    },
    {
        label: "Silver",
        value: 20000,
    },
    {
        label: "Gold",
        value: 30000,
    },
]

export const DEFAULT_DONATION_PERCENTAGES = [
    {
        label: "",
        percentage_label: "",
        percentage_decimal_value: "",
    },
]

export const DEFAULT_NEW_DONATION_AMOUNT = {
    label: "",
    value: 0,
}

export const DEFAULT_NEW_DONATION_PERCENTAGE = {
    label: "",
    percentage_label: "",
    percentage_decimal_value: "",
}

export const DONATION_FORM_AMOUNT_MAX_CENTS = 500000 // $5,000

export default helpText
